import { t } from '../i18n';
import {
  Cart,
  CartOrderLine,
  CartPromotion,
  CartSellerLine,
  ChildOrderLine,
  PartOrderBenefit,
} from '../types/cart';
import { PromotionItemProps } from '@teko/ecommerce/mobile';
import {
  CartLineGiftProps,
  CartLineProductProps,
  CartVoucher,
} from '@teko/ui-cart';
import get from 'lodash.get';
import { getProductUrl, getLimitShippingLocations } from './products';
import { PromotionsMap, CartContextState } from '../contexts';
import { ApplicableService, PaymentMethod } from '../types';
import { formatCurrency } from './common';
import { MIN_INSTALLMENT_ORDER_VALUE } from '../constants';
import { LinkInterface } from '@teko/types';
import { getSessionStorageOver18YearOld } from '@teko/utils';

export const isDigitalLicenseProduct = (attributes: any) => {
  if (!attributes) return false;
  const { values } = (attributes || []).find(
    (item: any) => item.code === 'phanmem_digitallicense',
  ) || { values: [] };
  return !!values.find(
    (item: any) => item.optionId === 9772 && item.value === t('Có'),
  );
};
const getComboItems = (product: any, items: any) => {
  return items
    .filter(
      (orderLine: any) =>
        orderLine.parentSku === product.sku &&
        orderLine.parentId === product.lineItemId,
    )
    .map((orderLine: any) => {
      return {
        name: orderLine.name,
        displayName: orderLine.displayName,
        price: orderLine.price,
        unitPrice: 0,
        quantity: orderLine.quantity,
        lineItemId: orderLine.lineItemId,
        selected: orderLine.selected,
        sku: orderLine.sku,
        isGift: orderLine.isGift,
        isCombo: orderLine.isCombo,
      };
    });
};
export const cookOrderLineToShow = (
  orderLine: any,
  products?: any,
  orderLines?: any,
  disableProductPromo?: boolean,
  orderPromotion?: any,
) => {
  const orderLineUnitPrice =
    products?.find((product: any) => product.sku === orderLine.sku)
      ?.supplierSalePrice || orderLine.unitPrice;
  const productAttributes = products?.find(
    (product: any) => product.sku === orderLine.sku,
  )?.attributes;
  const isLicense = isDigitalLicenseProduct(productAttributes);
  const comboItems = orderLine.isCombo
    ? getComboItems(orderLine, orderLines || [])
    : [];
  let orderLineDefaultMoneyDiscount = 0;
  if (orderPromotion) {
    const defaultMoneyDiscount = orderPromotion.filter((promotion: any) =>
      promotion.applyOn.length === 0
        ? false
        : promotion.applyOn.filter(
            (el: any) => el.lineItemId === orderLine.lineItemId,
          ).length > 0 &&
          (promotion.basePromotion?.isDefault ||
            promotion.type === 'flash_sale'),
    );
    orderLineDefaultMoneyDiscount = defaultMoneyDiscount.reduce(
      (sum: number, current: any) => {
        return sum + current.discount / orderLine.quantity;
      },
      0,
    );
  }
  return {
    ...orderLine,
    lastRefetchTime: Date.now(),
    price: disableProductPromo
      ? orderLineUnitPrice - orderLineDefaultMoneyDiscount
      : orderLine.price,
    unitPrice: orderLineUnitPrice,
    promoDiscount:
      orderLineUnitPrice - orderLineDefaultMoneyDiscount - orderLine.price,
    selected: orderLine.isSelected,
    isLicense,
    comboItems,
    hasNotDefaultPromo:
      orderPromotion.filter((promotion: any) =>
        promotion.applyOn.length === 0
          ? false
          : promotion.applyOn?.filter(
              (el: any) => el.lineItemId === orderLine.lineItemId,
            ).length > 0 && promotion.basePromotion?.isDefault === false,
      ).length > 0,
  };
};
export const groupCartBySeller = (
  orderLines: CartOrderLine[],
  promotions: CartPromotion[] = [],
): CartSellerLine[] => {
  const allSellers = [
    ...new Set(orderLines.map((line: CartOrderLine) => line.sellerId)),
  ];
  return allSellers.map((id) => {
    const lines = orderLines.filter(
      (orderLine: CartOrderLine) =>
        orderLine.sellerId === id && !orderLine.childItems.length,
    );
    return {
      seller: {
        id,
        ...get(lines, '0.product.productInfo.seller', {
          displayName: '',
          logo: '',
        }),
      },
      sellerId: id,
      grandTotal: lines.reduce(
        (total, line) => total + (line.isSelected ? line.rowTotal : 0),
        0,
      ),
      totalPriceDifference: lines.reduce(
        (total, line) =>
          total +
          ((line.isSelected && line?.consultationServiceFee) || 0) *
            line.quantity,
        0,
      ),
      orderLines: lines.filter((line) => line.childItems.length === 0),
      promotions: promotions.filter(
        (promo) =>
          promo.applyType === 'product' &&
          (promo?.sellerIds?.[0] === id || promo?.sellerId === id),
      ),
      selectedCount: lines.filter((line: CartOrderLine) => line.isSelected)
        .length,
      totalCount: lines.length,
    };
  });
};
export const getUnitPrice = (orderLine: CartOrderLine): number => {
  const childItems = orderLine.childItems || [];
  if (!childItems.length)
    return Number(
      orderLine?.product?.prices?.[0]?.supplierRetailPrice ||
        orderLine.unitPrice,
    );
  const parentQuantity = orderLine.quantity;
  return childItems.reduce(
    (total, item) =>
      total +
      Number(
        item?.product?.prices?.[0]?.supplierRetailPrice || orderLine.unitPrice,
      ) *
        (item?.quantity / parentQuantity || 0),
    0,
  );
};
export const getComboBenefits = (
  comboProducts: CartOrderLine[],
  promotions: CartPromotion[],
): PartOrderBenefit[] => {
  const childItemIdMap = comboProducts[0].childItems
    .map((item) => item.lineItemId)
    .reduce((dict, id) => {
      return {
        ...dict,
        [id]: true,
      };
    }, {});
  const childItemPromotions = promotions
    .filter((promo) => promo.applyType === 'product')
    .reduce((result, promo) => {
      const applyOnIdList = (promo.applyOn || []).map(
        (target) => target.lineItemId,
      );
      const isValid = applyOnIdList.some((id) => childItemIdMap[id]);
      return [...result, isValid && promo];
    }, []);
  const comboPromotions = [
    promotions.find((promo) => promo.applyType === 'combo'),
  ];
  const totalPromotions = [...comboPromotions, ...childItemPromotions];
  return totalPromotions.map((promo) => {
    if (!promo) return [];
    if (!promo.gifts?.length) return promo.basePromotion.benefit;
    const skuQuantityMap = promo.gifts.reduce((map, { sku, quantity }) => {
      return {
        ...map,
        [sku]: quantity,
      };
    }, {});
    return {
      ...promo.basePromotion.benefit,
      gifts: promo.basePromotion.benefit.gifts.map((gift) => {
        return {
          ...gift,
          quantity: skuQuantityMap[gift.sku],
        };
      }),
    };
  });
};

interface ConvertCartLineProductProps extends CartLineProductProps {
  link: LinkInterface;
}

export const convertCartLineProduct = (
  orderLine: CartOrderLine,
  promotionsMap: PromotionsMap,
): ConvertCartLineProductProps => {
  if (orderLine.isGift && orderLine.parentSku) return;
  const promotion = promotionsMap[orderLine.lineItemId];
  const imageUrl = get(orderLine, 'product.productInfo.imageUrl');
  const childItems = orderLine.childItems || [];
  const unitPrice = getUnitPrice(orderLine);
  return {
    ...orderLine,
    sku: orderLine.sku,
    name: orderLine.displayName || orderLine.name,
    price: orderLine.price,
    discountPrice: orderLine.price,
    unitPrice: unitPrice,
    imageUrl: imageUrl,
    quantity: orderLine.quantity,
    quantityDecimal: orderLine.quantityDecimal,
    selected: orderLine.isSelected,
    variants: [],
    gifts: promotion?.gifts?.map((gift) => ({
      name: gift.name,
      imageUrl: gift.imageUrl,
      isDefault: gift.isDefault,
      quantity: gift.quantity,
      sku: gift.sku,
    })),
    voucher: promotion?.voucher,
    link: {
      as: getProductUrl(orderLine.displayName || orderLine.name, orderLine.sku),
      href: { pathname: '/products/[sku]', query: { sku: orderLine.sku } },
      passHref: true,
    },
    childItems: childItems.map((item: ChildOrderLine) => {
      const {
        sku,
        name,
        quantity,
        price,
        unitDiscount,
        unitPrice,
        displayPriority,
        product,
      } = item;
      return {
        sku: sku,
        name: name,
        quantity: quantity,
        price: price,
        discountPrice: unitDiscount,
        unitPrice: unitPrice,
        displayPriority: displayPriority,
        imageUrl: product?.productInfo?.imageUrl,
      };
    }),
  };
};
export const getComboGifts = (
  benefits: PartOrderBenefit[],
): CartLineGiftProps[] => {
  const nestedBenefit = benefits.map((benefit) => {
    return benefit.gifts;
  });
  return [].concat(...nestedBenefit).filter(Boolean);
};
export const getComboVoucher = (
  benefits: PartOrderBenefit[],
): CartVoucher | null => {
  const validBenefit = benefits.find((benefit) => benefit.voucher?.quantity);
  return validBenefit ? validBenefit.voucher : null;
};
const sortItems = (childItems: any): any => {
  return childItems.sort((left, right) =>
    left.displayPriority > right.displayPriority ? 1 : -1,
  );
};
export const getComboName = (childItems: any): string => {
  if (childItems.length === 0) return '';
  const primary = sortItems(childItems)[0];
  const comboLength = childItems.length;
  return comboLength === 1
    ? primary.name
    : t('{{PrimaryName}} và {{var0}} sản phẩm khác', {
        PrimaryName: primary.name,
        var0: comboLength - 1,
      });
};
export const getComboImageUrl = (childItems: any): string[] => {
  if (childItems.length === 0) return [];
  return sortItems(childItems).map(
    (item: any) => item.imageUrl || item.product?.productInfo?.imageUrl,
  );
};
export const cookCart = (cart: Cart): any => {
  if (!cart || Object.keys(cart).length === 0) {
    return null;
  }
  const cartProducts = cart.orderLines
    .filter((el) => el.product)
    .map((el) => el.product);
  const orderLinesToShow = cart.orderLines.map((orderLine) =>
    cookOrderLineToShow(
      orderLine,
      cartProducts,
      cart.orderLines,
      false,
      cart.promotions,
    ),
  );
  return {
    ...cart,
    deliveryMethod: 'Standard',
    appliedOrderCoupon: null,
    disableProductPromo: false,
    isCouponDisabled: false,
    replaceCouponConfirm: null,
    // TODO: merge orderLines and orderLines2
    orderLines: orderLinesToShow,
    promotions: cart.promotions.map((promotion: any) => {
      const promotionApplyOn = promotion.applyOn;
      const gifts = promotion.gifts;
      return {
        ...promotion,
        applyType: promotion.applyType,
        applyOn:
          promotionApplyOn.length === 0
            ? []
            : promotionApplyOn.map((applyOn: any) => ({
                ...applyOn,
                lineItemId: applyOn.lineItemId,
                quantity: applyOn.quantity,
              })),
        gifts:
          gifts.length === 0
            ? []
            : gifts.map((gift: any) => ({
                sku: gift.sku,
                name: gift.name,
                quantity: gift.quantity,
                isDefault: promotion.basePromotion?.isDefault,
              })),
      };
    }),
  };
};
export const convertOrderPromo = (
  orderPromotion: any,
): PromotionItemProps[] => {
  const { discount, gifts, voucher } = get(
    orderPromotion,
    'basePromotion.benefit',
    {},
  );
  const result = [];
  if (orderPromotion.discount && discount) {
    result.push({
      name: t('Giảm giá {{var0}}', {
        var0: discount.percent
          ? `${discount.percent}% tối đa ${formatCurrency(
              discount.maxAmount,
              true,
            )}`
          : formatCurrency(discount.flat),
      }),
      value: orderPromotion.discount,
      type: 'coupon',
    });
  }
  if (gifts) {
    gifts.forEach(({ name, sku, quantity }: any) => {
      result.push({
        name,
        sku,
        quantity,
        type: 'product',
      });
    });
  }
  if (voucher) {
    result.push({
      name: voucher.name,
      quantity: voucher.quantity,
      type: 'voucher',
    });
  }
  return result;
};
export const needConfirm18YearsOld = (
  isAlcoholicCategory: boolean,
): boolean => {
  const isOver18YearsOld = getSessionStorageOver18YearOld();
  return !isOver18YearsOld && isAlcoholicCategory;
};

export const getNotSupportShippingProduct = (
  cart: CartContextState,
  provinceCode: string,
) => {
  const products = get(cart, 'order.orderLines', []);
  const notSupportProducts: string[] = [];
  products.forEach((product: CartOrderLine) => {
    const providerId = get(product, 'product.provider.id');
    if (providerId) {
      const limitProvinces = getLimitShippingLocations(providerId);
      if (
        limitProvinces &&
        !limitProvinces.some((province) => province.code === provinceCode)
      ) {
        notSupportProducts.push(product.displayName);
      }
    }
  });
  return notSupportProducts;
};

export const getSellerDisplayName = (
  id?: number,
  sellerLines?: CartSellerLine[],
) => {
  return sellerLines?.find(({ sellerId }) => id === sellerId)?.seller
    ?.displayName;
};
export const getSelectedCartPromo = (promotions: CartPromotion[]) => {
  return promotions?.find(
    (el) =>
      el.applyType === 'order' &&
      el.isDefault === false &&
      !el.coupon &&
      el.isSelected,
  );
};
export const getTotalCartDiscount = (cart: CartContextState) => {
  const promotions =
    cart?.order?.promotions?.filter(
      (el) =>
        el.applyType === 'order' &&
        el.isDefault === false &&
        !el.coupon &&
        el.isSelected,
    ) || [];
  return promotions.reduce((acc, curr) => {
    return (acc = acc + curr.discount);
  }, 0);
};

const getMaxProductQuantity = (orderLine: CartOrderLine) =>
  orderLine.product.totalAvailable || Number.MAX_SAFE_INTEGER;
const getMaxComboquantity = (
  comboLine: CartOrderLine,
  orderLineMaximumQuantity: number,
) =>
  // currently Cart Service only support int combo quantity
  Math.floor(
    comboLine.childItems.reduce(
      (maxQuantity, { quantity, product: { totalAvailable } }) => {
        if (totalAvailable != null)
          return Math.min(
            (totalAvailable / quantity) * comboLine.quantity,
            maxQuantity,
          );
      },
      orderLineMaximumQuantity,
    ),
  );
export const getMaxItemLineQuantity = (
  orderLine: CartOrderLine,
  orderLineMaximumQuantity: number,
) => {
  const isCombo = orderLine.childItems.length > 0;
  return isCombo
    ? getMaxComboquantity(orderLine, orderLineMaximumQuantity)
    : getMaxProductQuantity(orderLine);
};
export const calculateShippingFeeDisplay = (
  service: ApplicableService,
  shippingFeeDiscount: number,
) => {
  let totalCurrency = 0;
  if (shippingFeeDiscount && service?.fee - shippingFeeDiscount > 0) {
    totalCurrency = service?.fee - shippingFeeDiscount;
  } else if (!shippingFeeDiscount) {
    totalCurrency = service?.fee;
  }
  return totalCurrency;
};

/**
 * return cart title base on orderId and isClone properties
 * @param orderId
 * @param isClone
 * @returns
 */
export const getCartTitle = (orderId: string, isClone: boolean): string => {
  // case checkout new order
  if (!orderId) {
    return t('Giỏ hàng');
  }

  // case clone order
  if (isClone) {
    return t(`Giỏ hàng (copy đơn {{orderId}})`, { orderId });
  }

  // case edit order
  return t(`Sửa đơn hàng {{orderId}}`, { orderId });
};

export const isInstallmentError = (
  remainPayment: number,
  paymentMethod: PaymentMethod,
): boolean => {
  if (paymentMethod?.methodGroupCode === 'INSTALLMENT') {
    return remainPayment < MIN_INSTALLMENT_ORDER_VALUE;
  } else {
    return false;
  }
};
